import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import LabelledInput from 'components/LabelledInput';
import Button from 'widgets/Button';
import colors from 'assets/styles/jsVariables/colors';
import Divider from 'widgets/Divider';
import AppleAuthButton from 'components/AppleAuthButton';
import GoogleAuthButton from 'components/GoogleAuthButton';
import Modal from 'components/Modal';
import ErrorBar from 'components/ErrorBar';
import Loader from 'components/Loader';
import { initRecurring } from 'libs/activate';
import { ResetEmail, RetryLogin, Verification } from 'components/ModalContent';
import { getGoogleAuthCbParam, processAuthInfoForAdditionalInfo,
  authCallback, getValuesFromUrl } from 'libs/auth';
import AuthFooter from './AuthFooter';
import defaultStyle from './style';
import {
  USER_INFO_KEYS, ERROR_INFO_KEYS, AUTH_FORM_ADDITIONAL_INFO_ERROR_CONFIG,
  AUTH_FORM_LOGIN_ERROR_CONFIG, RESET_EMAIL_ERROR_CONFIG, AUTH_FORM_SIGNUP_ERROR_CONFIG,
  checkErrors, AUTH0_CONNECTIONS
} from './config';
import './Auth.scss';

function getMissedAdditionalInfo(userInfo, additionalInfo) {
  const missedAddtionalInfo = {};
  if (!additionalInfo[USER_INFO_KEYS.FIRST_NAME]) {
    missedAddtionalInfo[USER_INFO_KEYS.FIRST_NAME] = userInfo[USER_INFO_KEYS.FIRST_NAME];
  }

  if (!additionalInfo[USER_INFO_KEYS.LAST_NAME]) {
    missedAddtionalInfo[USER_INFO_KEYS.LAST_NAME] = userInfo[USER_INFO_KEYS.LAST_NAME];
  }

  if (!additionalInfo[USER_INFO_KEYS.DOB]) {
    missedAddtionalInfo[USER_INFO_KEYS.DOB] = userInfo[USER_INFO_KEYS.DOB];
  }

  missedAddtionalInfo[USER_INFO_KEYS.ACCESS_TOKEN] = userInfo[USER_INFO_KEYS.ACCESS_TOKEN];

  return missedAddtionalInfo;
}

class Auth extends React.Component {
  constructor(props) {
    super(props);
    const urlValues = getValuesFromUrl(['email', 'chargebee_user_id', 'sign_up']);

    const signupParameter = () => {
      if (urlValues.sign_up === "true") {
        return true;
      } else {
        return false;
      }
    };

    this.state = {
      isSignup: signupParameter(),
      isAdditionalInfoMissed: false,
      forgotPasswordModalOpen: false,
      isRecurrLoginRetry: false,
      emailVerifyModalOpen: false,
      additionalInfo: {},
      userInfo: {
        [USER_INFO_KEYS.FIRST_NAME]: '',
        [USER_INFO_KEYS.LAST_NAME]: '',
        [USER_INFO_KEYS.DOB]: '',
        [USER_INFO_KEYS.EMAIL]: urlValues.email,
        [USER_INFO_KEYS.PASSWORD]: '',
        [USER_INFO_KEYS.RESET_EMAIL]: '',
        [USER_INFO_KEYS.ACCESS_TOKEN]: '',
        [USER_INFO_KEYS.CHARGEBEE_USER_ID]: urlValues.chargebee_user_id,
      },
      errorInfo: {
        [ERROR_INFO_KEYS.AUTH_FORM]: {}, // for login/signup form
        [ERROR_INFO_KEYS.RESET_EMAIL_FORM]: {}, // for reset email form
        errorConfigAuth: AUTH_FORM_LOGIN_ERROR_CONFIG,
      },
      errorBar: {
        show: false,
        message: ''
      }
    };
    this.isAuthFormDirty = false;
    this.isResetEmailFormDirty = false;
    this.authInfo = {};
  }

  componentDidMount() {
    const params = getGoogleAuthCbParam();
    if (params) {
      const { id_token, access_token, error_description } = params;
      if (error_description) {
        this.showErrorBar({ message: error_description });
      } else {
        const authInfo = {
          idToken: id_token,
          accessToken: access_token
        };
        const additionalInfo = processAuthInfoForAdditionalInfo(authInfo);
        this.handleAddtionalInfoCb(additionalInfo);
      }
    }
  }

  setRecurrLoginRetry = () => {
    this.setState({ isRecurrLoginRetry: true });
  }

  resetRecurrLoginRetry = () => {
    this.setState({ isRecurrLoginRetry: false });
  }

  handleAuthSuccess = ({ accessToken }) => {
    authCallback({ accessToken });
  }

  handleChange = (e, cb) => {
    const { userInfo } = this.state;
    userInfo[e.target.name] = e.target.value;
    this.setState({ userInfo }, cb);
  }

  handleAuthFormInputChange = e => this.handleChange(e, () => {
    if (this.isAuthFormDirty) {
      const { errorInfo: { errorConfigAuth } } = this.state;
      this.validateFields(errorConfigAuth);
    }
  });

  handleResetFormInputChange = e => this.handleChange(e, () => {
    if (this.isResetEmailFormDirty) {
      this.validateFields(RESET_EMAIL_ERROR_CONFIG);
    }
  })

  handleDobChange = (e) => {
    const { userInfo } = this.state;
    const dob = userInfo[USER_INFO_KEYS.DOB];
    let { value = '' } = e.target;

    if ((value.length === 2 || value.length === 5) && (dob.length < value.length)) {
      value = `${value}/`;
    }
    e.target.value = value;
    this.handleAuthFormInputChange(e);
  }

  handleAuthFormSave = () => {
    const { errorInfo: { errorConfigAuth } } = this.state;
    const errorInfo = this.validateFields(errorConfigAuth);

    this.isAuthFormDirty = true;

    if (Object.keys(errorInfo[ERROR_INFO_KEYS.AUTH_FORM]).length) {
      return;
    }

    const {
      isSignup, userInfo, isAdditionalInfoMissed, additionalInfo
    } = this.state;
    if (isAdditionalInfoMissed) {
      const _userInfo = getMissedAdditionalInfo(userInfo, additionalInfo);
      this.props.saveAddtionalInfoAuth(_userInfo, this.handleSaveCallback);
    } else if (isSignup) {
      this.props.signupAuth(userInfo, this.handleSaveCallback);
    } else {
      this.props.loginAuth(userInfo, this.handleSaveCallback);
    }
  }

  handleResetEmailPasswordSave = () => {
    const errorInfo = this.validateFields(RESET_EMAIL_ERROR_CONFIG);
    this.isResetEmailFormDirty = true;

    if (Object.keys(errorInfo[ERROR_INFO_KEYS.RESET_EMAIL_FORM]).length) {
      return;
    }

    const email = this.state.userInfo[USER_INFO_KEYS.RESET_EMAIL];

    this.props.resetEmailAuth({ email }, this.handleSaveCallback);
  }

  handleAddtionalInfoCb = (additionalInfo = {}) => {
    const { fields = {} } = additionalInfo;
    if (additionalInfo.isAdditionalInfoMissed && fields.isSignup) {
      const { userInfo, errorInfo } = this.state;
      errorInfo.errorConfigAuth = AUTH_FORM_ADDITIONAL_INFO_ERROR_CONFIG;

      this.setState({
        isAdditionalInfoMissed: true,
        userInfo: {
          ...userInfo,
          ...fields,
          [USER_INFO_KEYS.ACCESS_TOKEN]: additionalInfo.accessToken
        },
        isSignup: false,
        additionalInfo: fields,
        errorInfo
      });
    } else {
      this.handleAuthSuccess({ accessToken: additionalInfo.accessToken });
    }
  }

  handleSocialAuthorisationContinue = (connection) => {
    const { userInfo } = this.state;

    this.props.continueWithSocial(connection, userInfo);
  }

  handleSaveCallback = ({
    message, closeResetModal, recurringLogin, additionalInfo, closeEmailVerifyModal
  }) => {
    const { userInfo } = this.state;

    if (recurringLogin) {
      initRecurring({
        initCb: this.recurringInitCb,
        endCb: this.recurringEndCb,
        api: this.props.loginAuthRecurring,
        apiParams: [userInfo],
        successCb: this.handleSaveCallback,
      });
    }

    if (message) {
      this.showErrorBar({ message });
    }

    if (closeResetModal) {
      this.closeResetPasswordModal();
    }

    if (closeEmailVerifyModal) {
      this.closeEmailVerifyModal();
    }

    if (additionalInfo) {
      this.handleAddtionalInfoCb(additionalInfo);
    }
  }

  handleRetryRecurrLogin = () => {
    const { userInfo } = this.state;
    this.resetRecurrLoginRetry();
    initRecurring({
      initCb: this.recurringInitCb,
      endCb: this.recurringEndCb,
      api: this.props.loginAuthRecurring,
      apiParams: [userInfo],
      successCb: this.handleSaveCallback,
      isActivateStraightAway: true,
    });
  }

  recurringInitCb = () => {
    this.showEmailVerifyModal();
  }

  recurringEndCb = () => {
    this.setRecurrLoginRetry();
  }

  showResetPasswordModal = () => {
    this.setState({ forgotPasswordModalOpen: true });
  }

  closeResetPasswordModal = () => {
    this.setState({ forgotPasswordModalOpen: false });
  }

  showEmailVerifyModal = () => {
    this.setState({ emailVerifyModalOpen: true });
  }

  closeEmailVerifyModal = () => {
    this.setState({ emailVerifyModalOpen: false });
  }

  validateFields = (config = {}) => {
    this.handleErrorBarClose();

    const { userInfo, errorInfo } = this.state;
    const { errorFields } = config;

    const errors = checkErrors(errorFields, userInfo);
    errorInfo[config.errorKey] = errors;

    this.setState({ errorInfo });
    return errorInfo;
  }

  toggleView = () => {
    const { isSignup, errorInfo } = this.state;
    errorInfo.errorConfigAuth = !isSignup ?
      AUTH_FORM_SIGNUP_ERROR_CONFIG :
      AUTH_FORM_LOGIN_ERROR_CONFIG;
    errorInfo[ERROR_INFO_KEYS.AUTH_FORM] = {};

    this.setState({ isSignup: !isSignup, errorInfo });
  }

  handleErrorBarClose = () => {
    const { errorBar } = this.state;
    if (errorBar.show) {
      const _errorBar = {
        show: false
      };
      this.setState({ errorBar: _errorBar });
    }
  }

  showErrorBar = ({ message }) => {
    const errorBar = {
      show: true,
      message
    };
    this.setState({ errorBar });
  }

  render() {
    const {
      isSignup, errorBar, emailVerifyModalOpen,
      userInfo, errorInfo, forgotPasswordModalOpen,
      isRecurrLoginRetry, isAdditionalInfoMissed,
      additionalInfo
    } = this.state;

    const {
      auth, logo, verifyEmailClassName, resetEmailClassName,
      themeColor, intl
    } = this.props;

    if (auth.isProcessingAuth) {
      return (<Loader />);
    }

    const resetEmailErr = errorInfo[ERROR_INFO_KEYS.RESET_EMAIL_FORM][USER_INFO_KEYS.RESET_EMAIL];
    const errorInfoAuthForm = errorInfo[ERROR_INFO_KEYS.AUTH_FORM] || {};
    let EmailVerifyTitle = <FormattedMessage id="components.auth.verification.title" />;
    let EmailVerifyContent = Verification;
    if (isRecurrLoginRetry) {
      EmailVerifyTitle = (
        <span
          style={{ fontFamily: 'museosansrounded_500', color: colors.darkerGrey, fontSize: '22px' }}
        >
          <FormattedMessage id="components.auth.retryLogin.title" />
        </span>
      );

      EmailVerifyContent = () => (
        <RetryLogin
          onClick={this.handleRetryRecurrLogin}
          rootStyleButton={defaultStyle.AuthButtonStyle}
          labelStyleButton={defaultStyle.AuthButtonLabelStyle}
        />
      );
    }
    return (
      <div>
        <ErrorBar errorBar={errorBar} onClose={this.handleErrorBarClose} />
        <picture>
          <source media="(min-width: 1023px)" srcSet="/images/sub_activate_img_desktop.png" />
          <source media="(min-width: 767px)" srcSet="/images/sub_activate_Img_tab.png" />
          <img src="/images/sub_activate_img_mob.png" alt="activate" className="Auth__BannerImage" width="100%" />
        </picture>
        <div className="Auth">
          <Modal
            title={EmailVerifyTitle}
            showCross={false}
            className={verifyEmailClassName}
            open={emailVerifyModalOpen}
            {...defaultStyle.modalStyleVerifyEmail}
          >
            <div className="Auth__Modal__EmailVerify__Content">
              <EmailVerifyContent themeColor={themeColor} />
            </div>
          </Modal>
          <Modal
            title={<FormattedMessage id="components.auth.modal.resetEmail.title" />}
            open={forgotPasswordModalOpen}
            toggleModal={this.closeResetPasswordModal}
            {...defaultStyle.modalStyleResetPasswordEmail}
            className={resetEmailClassName}
          >
            <div
              className="Auth__Modal__ResetEmail__Form__Content"
            >
              <ResetEmail
                input={{
                  isError: !!resetEmailErr,
                  name: USER_INFO_KEYS.RESET_EMAIL,
                  onChange: this.handleResetFormInputChange,
                  iconName: 'email-grey',
                  placeholder: intl.formatMessage({ id: 'components.auth.modal.resetEmail.form.content.email.placeholder' }),
                  labelText: intl.formatMessage({ id: 'components.auth.modal.resetEmail.form.content.email.label' }),
                  value: userInfo[USER_INFO_KEYS.RESET_EMAIL],
                  onEnter: this.handleResetEmailPasswordSave,
                  cssStylesInput: defaultStyle.inputStyle,
                  errorText: resetEmailErr && intl.formatMessage({ id: resetEmailErr }),
                  autoFocus: true
                }}
                action={{
                  cssAuthButtonRootStyle: defaultStyle.AuthButtonStyle,
                  cssAuthButtonCssLabelStyle: defaultStyle.AuthButtonLabelStyle,
                  isProcessingAuth: auth.isProcessingAuth,
                  onClick: this.handleResetEmailPasswordSave
                }}
              />
            </div>
          </Modal>
          <div className="Auth__FormCardWrap">
            <div className="Auth__FormCard">
              <div className="Auth__FormCard__Logo">
                <img src={logo} alt="activate" className="Auth__FormCard__Logo__Image" />
              </div>
              <div className="Auth__Description">
                {
                  isAdditionalInfoMissed &&
                  <React.Fragment>
                    {
                      (
                        !additionalInfo[USER_INFO_KEYS.FIRST_NAME] &&
                        !additionalInfo[USER_INFO_KEYS.DOB]
                      )
                      ? <FormattedMessage id="components.auth.formCard.description.nameAndDob" />
                      : !additionalInfo[USER_INFO_KEYS.DOB]
                        ? <FormattedMessage id="components.auth.formCard.description.dob" />
                        : <FormattedMessage id="components.auth.formCard.description.name" />
                    }
                  </React.Fragment>
                }
              </div>
              {
                !isAdditionalInfoMissed &&
                <React.Fragment>
                  <React.Fragment>
                    <div className="Auth__FormCard__TabWrap">
                      <div
                        role="button"
                        tabIndex="0"
                        onClick={this.toggleView}
                        className={`Auth__FormCard__TabWrap__Tab--Left ${(!isSignup && 'selected') || ''}`}
                      >
                        {
                          !isSignup &&
                          <img
                            className="Tabicon"
                            height="12"
                            width="12"
                            src="/images/icon/checkmark_white.png"
                            alt="icon"
                          />
                        }
                        <FormattedMessage id="components.auth.formCard.tab.signInLabel" />
                      </div>
                      <div
                        role="button"
                        tabIndex="0"
                        onClick={this.toggleView}
                        className={`Auth__FormCard__TabWrap__Tab--Right ${(isSignup && 'selected') || ''}`}
                      >
                        {
                          !!isSignup &&
                          <img
                            className="Tabicon"
                            height="12"
                            width="12"
                            src="/images/icon/checkmark_white.png"
                            alt="icon"
                          />
                        }
                        <FormattedMessage id="components.auth.formCard.tab.signUpLabel" />
                      </div>
                    </div>

                  </React.Fragment>
                  <React.Fragment>
                    <div className="Auth__FormCard__Action">
                      <div className="Auth__FormCard__Action__SocialAuth">
                        <GoogleAuthButton
                          onClick={() => this.handleSocialAuthorisationContinue(AUTH0_CONNECTIONS.google)}
                          disabled={auth.isProcessingAuth}
                          isSignup={isSignup}
                        />
                      </div>
                      <div className="Auth__FormCard__Action__SocialAuth">
                        <AppleAuthButton
                          onClick={() => this.handleSocialAuthorisationContinue(AUTH0_CONNECTIONS.apple)}
                          disabled={auth.isProcessingAuth}
                          isSignup={isSignup}
                        />
                      </div>
                    </div>
                    <div className="Auth__FormCard__Action__Separator">
                      <Divider
                        style={{
                          height: '1px',
                          color: colors.lightestGrey,
                          margin: '25px 82px 25px'
                        }}
                      />
                      <span className="Auth__FormCard__Action__Separator__Text">
                        <FormattedMessage id="components.auth.formCard.separatorText" />
                      </span>
                    </div>
                    {
                      isSignup &&
                      <div className="Auth__SectionDetails">
                        <span>
                          <FormattedMessage id="components.auth.formCard.action.sectionDetails" />
                        </span>
                      </div>
                    }
                  </React.Fragment>
                </React.Fragment>
              }

              <React.Fragment>
                {
                  (
                    isSignup ||
                    (isAdditionalInfoMissed && !additionalInfo[USER_INFO_KEYS.DOB])
                  ) &&
                  <div className="Auth__InputWrap">
                    <LabelledInput
                      isError={!!errorInfoAuthForm[USER_INFO_KEYS.DOB]}
                      name={USER_INFO_KEYS.DOB}
                      onChange={this.handleDobChange}
                      iconName="dob"
                      placeholder={intl.formatMessage({ id: 'components.auth.inputWrap.dob.placeholder' })}
                      labelText={intl.formatMessage({ id: 'components.auth.inputWrap.dob.label' })}
                      value={userInfo[USER_INFO_KEYS.DOB]}
                      onEnter={this.handleAuthFormSave}
                      errorText={errorInfoAuthForm[USER_INFO_KEYS.DOB]
                        && intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.DOB] })}
                      autoFocus
                    />
                  </div>
                }
                {
                  (
                    isSignup ||
                    (isAdditionalInfoMissed && !additionalInfo[USER_INFO_KEYS.FIRST_NAME])
                  ) &&
                  <div className="Auth__NameSection">
                    <div className="Auth__InputWrap">
                      <LabelledInput
                        isError={!!errorInfoAuthForm[USER_INFO_KEYS.FIRST_NAME]}
                        name={USER_INFO_KEYS.FIRST_NAME}
                        onChange={this.handleAuthFormInputChange}
                        iconName="profile"
                        placeholder={intl.formatMessage({ id: 'components.auth.inputWrap.firstName.placeholder' })}
                        labelText={intl.formatMessage({ id: 'components.auth.inputWrap.firstName.label' })}
                        value={userInfo[USER_INFO_KEYS.FIRST_NAME]}
                        onEnter={this.handleAuthFormSave}
                        errorText={errorInfoAuthForm[USER_INFO_KEYS.FIRST_NAME] &&
                          intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.FIRST_NAME] })}
                      />
                    </div>
                    <div className="Auth__InputWrap">
                      <LabelledInput
                        isError={!!errorInfoAuthForm[USER_INFO_KEYS.LAST_NAME]}
                        name={USER_INFO_KEYS.LAST_NAME}
                        onChange={this.handleAuthFormInputChange}
                        placeholder={intl.formatMessage({ id: 'components.auth.inputWrap.lastName.placeholder' })}
                        labelText=""
                        value={userInfo[USER_INFO_KEYS.LAST_NAME]}
                        onEnter={this.handleAuthFormSave}
                        errorText={errorInfoAuthForm[USER_INFO_KEYS.LAST_NAME] &&
                          intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.LAST_NAME] })}
                      />
                    </div>
                  </div>
                }
              </React.Fragment>

              {
                !isAdditionalInfoMissed &&
                <React.Fragment>
                  <div className={isSignup ? 'Auth__InputWrap' : 'Auth__InputWrap--Email--SignIn'}>
                    <LabelledInput
                      isError={!!errorInfoAuthForm[USER_INFO_KEYS.EMAIL]}
                      name={USER_INFO_KEYS.EMAIL}
                      onChange={this.handleAuthFormInputChange}
                      iconName="email-grey"
                      placeholder={intl.formatMessage({ id: 'components.auth.inputWrap.email.placeholder' })}
                      labelText={intl.formatMessage({ id: 'components.auth.inputWrap.email.label' })}
                      id="AuthEmail"
                      value={userInfo[USER_INFO_KEYS.EMAIL]}
                      onEnter={this.handleAuthFormSave}
                      errorText={errorInfoAuthForm[USER_INFO_KEYS.EMAIL] &&
                        intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.EMAIL] })}
                      autoFocus
                    />
                  </div>
                  <div className="Auth__InputWrap LabelledInput__Input--Password">
                    <LabelledInput
                      isError={!!errorInfoAuthForm[USER_INFO_KEYS.PASSWORD]}
                      name={USER_INFO_KEYS.PASSWORD}
                      onChange={this.handleAuthFormInputChange}
                      type="password"
                      placeholder={intl.formatMessage({ id: 'components.auth.inputWrap.password.placeholder' })}
                      id="AuthPassword"
                      labelText={intl.formatMessage({ id: 'components.auth.inputWrap.password.label' })}
                      value={userInfo[USER_INFO_KEYS.PASSWORD]}
                      onEnter={this.handleAuthFormSave}
                      iconName="lock"
                      errorText={errorInfoAuthForm[USER_INFO_KEYS.PASSWORD] &&
                        intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.PASSWORD] })}
                    />
                  </div>
                </React.Fragment>
              }
              <div className="Auth__FormCard__Action">
                <Button
                  id="AuthAction"
                  cssStyles={{
                    root: defaultStyle.AuthButtonStyle,
                    label: defaultStyle.AuthButtonLabelStyle,
                  }}
                  className={`Auth__ActionButton ${(auth.isProcessingAuth && 'Disable') || ''}`}
                  disabled={auth.isProcessingAuth}
                  onClick={this.handleAuthFormSave}
                >
                  {
                    isAdditionalInfoMissed
                    ? <FormattedMessage id="components.auth.formCard.action.additionalInfoMissed.buttonLabel" />
                    : (isSignup ?
                      <FormattedMessage id="components.auth.formCard.action.signUp.buttonLabel" /> :
                      <FormattedMessage id="components.auth.formCard.action.signIn.buttonLabel" />)
                  }
                </Button>
              </div>
              <div className="Auth__FormCard__Note">
                {
                  (!isSignup && !isAdditionalInfoMissed) &&
                  <div>
                    <span
                      role="button"
                      tabIndex="0"
                      className="Auth__FormCard__Note__Link"
                      onClick={this.showResetPasswordModal}
                      style={{
                        outline: 'none'
                      }}
                    >
                      <FormattedMessage id="components.auth.formCard.resetPassword.link" />
                    </span>
                  </div>
                }
              </div>
            </div>
            <div className="Auth__Footer">
              <AuthFooter />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

Auth.propTypes = {
  saveAddtionalInfoAuth: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  signupAuth: PropTypes.func.isRequired,
  loginAuth: PropTypes.func.isRequired,
  continueWithSocial: PropTypes.func.isRequired,
  resetEmailAuth: PropTypes.func.isRequired,
  loginAuthRecurring: PropTypes.func.isRequired,
  logo: PropTypes.string.isRequired,
  resetEmailClassName: PropTypes.string,
  verifyEmailClassName: PropTypes.string,
  style: PropTypes.object,
  themeColor: PropTypes.object,
  intl: PropTypes.object.isRequired
};

Auth.defaultProps = {
  style: {},
  themeColor: {},
  verifyEmailClassName: '',
  resetEmailClassName: ''
};

export default injectIntl(Auth);



// WEBPACK FOOTER //
// ./src/components/Auth/Auth.jsx